import { getProductReviews, getAverageRating, getReviewCount } from '../../mocks/reviews';

interface ProductReviewsProps {
  productId: number;
}

const ProductReviews = ({ productId }: ProductReviewsProps) => {
  const reviews = getProductReviews(productId);
  const averageRating = getAverageRating(productId);
  const reviewCount = getReviewCount(productId);

  if (reviews.length === 0) {
    return (
      <div className="bg-gray-50 rounded-xl p-8 text-center">
        <i className="ri-star-line text-4xl text-gray-300 mb-3"></i>
        <p className="text-gray-500">No reviews yet. Be the first to review this product!</p>
      </div>
    );
  }

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <i
            key={star}
            className={`${
              star <= rating ? 'ri-star-fill text-yellow-400' : 'ri-star-line text-gray-300'
            }`}
          ></i>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Rating Summary */}
      <div className="bg-gradient-to-br from-primary/5 to-accent-green/5 rounded-xl p-6">
        <div className="flex items-center gap-6">
          <div className="text-center">
            <div className="text-5xl font-bold text-gray-900 mb-2">{averageRating}</div>
            <div className="flex justify-center mb-2">{renderStars(Number(averageRating))}</div>
            <p className="text-sm text-gray-600">{reviewCount} {reviewCount === 1 ? 'review' : 'reviews'}</p>
          </div>
          <div className="flex-1">
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map((star) => {
                const count = reviews.filter((r) => r.rating === star).length;
                const percentage = (count / reviews.length) * 100;
                return (
                  <div key={star} className="flex items-center gap-3">
                    <span className="text-sm text-gray-600 w-8">{star} ★</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-yellow-400 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600 w-12 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Individual Reviews */}
      <div className="space-y-4">
        {reviews.map((review) => (
          <div key={review.id} className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-semibold text-gray-900">{review.userName}</span>
                  {review.verified && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                      <i className="ri-checkbox-circle-fill"></i>
                      Verified Purchase
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {renderStars(review.rating)}
                  <span className="text-sm text-gray-500">{review.date}</span>
                </div>
              </div>
            </div>
            <p className="text-gray-700 leading-relaxed">{review.comment}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductReviews;
